import { useState, useMemo, useCallback } from 'react';
import { calculateAll, getDefaultInputs } from '../lib/calculator';
import { detectCounty } from '../lib/mn-data';
import type { CalculatorInputs, CalculatorResults } from '../lib/types';

export function useCalculator(initialInputs?: Partial<CalculatorInputs>) {
  const [inputs, setInputs] = useState<CalculatorInputs>({
    ...getDefaultInputs(),
    ...initialInputs,
  });

  const results: CalculatorResults = useMemo(() => calculateAll(inputs), [inputs]);

  const updateInput = useCallback(<K extends keyof CalculatorInputs>(
    key: K,
    value: CalculatorInputs[K]
  ) => {
    setInputs(prev => {
      const next = { ...prev, [key]: value };

      // Sync down payment % ↔ amount
      if (key === 'downPaymentPct' && typeof value === 'number') {
        next.downPaymentAmt = Math.round(next.homePrice * value / 100);
      } else if (key === 'downPaymentAmt' && typeof value === 'number') {
        next.downPaymentPct = next.homePrice > 0 ? parseFloat((value / next.homePrice * 100).toFixed(1)) : 0;
      } else if (key === 'homePrice' && typeof value === 'number') {
        next.downPaymentAmt = Math.round(value * next.downPaymentPct / 100);
      }

      // County auto-detection when address fields change
      if (key === 'propertyAddress' || key === 'propertyCity' || key === 'propertyZip') {
        const detection = detectCounty(next.propertyAddress, next.propertyCity, next.propertyZip);
        if (detection) {
          next.propertyTaxRate = parseFloat(detection.taxRate);
          next.mnCounty = detection.taxRate;
        }
      }

      // County dropdown changes tax rate
      if (key === 'mnCounty' && typeof value === 'string') {
        next.propertyTaxRate = parseFloat(value);
      }

      return next;
    });
  }, []);

  const updateInputs = useCallback((partial: Partial<CalculatorInputs>) => {
    setInputs(prev => ({ ...prev, ...partial }));
  }, []);

  return { inputs, results, updateInput, updateInputs, setInputs };
}
